import React, { useState, useEffect } from 'react';
import {
    Upload,
    FileText,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Search,
    Calendar,
    ChevronLeft,
    ChevronRight,
    ArrowRight,
    Landmark, // Importado
    Filter,
    X,
    ChevronDown,
    TrendingUp,
    TrendingDown,
    BarChart3,
    Link2,
    Unlink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { uploadBancolombiaCSV, getBancolombiaMovements, linkOrderToMovement, linkExpenseToMovement, getBancolombiaStats, tagMovement as tagMovementApi, bulkAutoTag as bulkAutoTagApi } from '../services/walletService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import OrderSearchSelect from '../components/OrderSearchSelect';
import ExpenseSearchSelect from '../components/ExpenseSearchSelect';
import MultiExpenseModal from '../components/MultiExpenseModal';

// Configuración centralizada de tags
const TAG_CONFIG = {
    intereses_banco:       { label: 'Intereses Banco', icon: '🏦', bg: 'bg-blue-100', text: 'text-blue-700' },
    gmf_4xmil:             { label: 'GMF (4x1000)', icon: '📊', bg: 'bg-purple-100', text: 'text-purple-700' },
    comision_transferencia: { label: 'Com. Transferencia', icon: '💳', bg: 'bg-orange-100', text: 'text-orange-700' },
    comision_consignacion:  { label: 'Com. Consignación', icon: '💳', bg: 'bg-orange-100', text: 'text-orange-700' },
    cuota_manejo:          { label: 'Cuota Manejo', icon: '💳', bg: 'bg-amber-100', text: 'text-amber-700' },
    iva_servicios:         { label: 'IVA Servicios', icon: '📊', bg: 'bg-indigo-100', text: 'text-indigo-700' },
    retencion_fuente:      { label: 'Retención Fuente', icon: '📋', bg: 'bg-yellow-100', text: 'text-yellow-700' },
    seguros_banco:         { label: 'Seguros Banco', icon: '🛡️', bg: 'bg-teal-100', text: 'text-teal-700' },
    publicidad_digital:    { label: 'Publicidad Digital', icon: '📱', bg: 'bg-pink-100', text: 'text-pink-700' },
    seguridad_social:      { label: 'Seg. Social', icon: '🏥', bg: 'bg-red-100', text: 'text-red-700' },
    impuesto_ica:          { label: 'Ind. y Comercio', icon: '🏛️', bg: 'bg-emerald-100', text: 'text-emerald-700' },
    otro_banco:            { label: 'Otro Bancario', icon: '🔹', bg: 'bg-gray-100', text: 'text-gray-700' }
};

const BancolombiaMovementsPage = () => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Filtros Avanzados
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, linked, pending
    const [tagFilter, setTagFilter] = useState('');

    // Multi-Expense Modal State
    const [multiExpenseModalOpen, setMultiExpenseModalOpen] = useState(false);
    const [selectedMovementForMulti, setSelectedMovementForMulti] = useState(null);

    // Nuevos Filtros Específicos (filterOrderNumber y filterCustomerName serán reemplazados por unifiedSearch)
    // const [filterOrderNumber, setFilterOrderNumber] = useState(''); // Reemplazado
    const [filterAmount, setFilterAmount] = useState('');
    // const [filterCustomerName, setFilterCustomerName] = useState(''); // Reemplazado

    // Estado para filtro unificado
    const [unifiedSearch, setUnifiedSearch] = useState('');

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 50;
    const totalPages = Math.ceil(total / limit);

    // Stats
    const [stats, setStats] = useState([]);
    const [expandedMonths, setExpandedMonths] = useState({});
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsCollapsed, setStatsCollapsed] = useState(false);

    // Debounce para búsqueda unificada y monto
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchMovements(1); // Reset to page 1 when filters change
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [unifiedSearch, filterAmount, startDate, endDate, statusFilter, tagFilter, searchTerm]);

    // Listen for real-time updates
    useEffect(() => {
        const handleUpdate = (event) => {
            console.log('🔔 [BANCOLOMBIA] Real-time update received:', event);
            // Re-fetch movements to show new link status
            fetchMovements(currentPage);
        };

        const socket = window.socket || (window.io ? window.io() : null);

        // Listen to global window event (custom event flow)
        window.addEventListener('bancolombia:movements_updated', handleUpdate);

        // Listen to direct socket event if available
        if (socket) {
            socket.on('bancolombia:movements_updated', handleUpdate);
        }

        return () => {
            window.removeEventListener('bancolombia:movements_updated', handleUpdate);
            if (socket) {
                socket.off('bancolombia:movements_updated', handleUpdate);
            }
        };
    }, [currentPage]); // Re-bind if page changes (though fetchMovements uses current props)

    useEffect(() => {
        fetchMovements(currentPage);
    }, [currentPage]);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setStatsLoading(true);
            const response = await getBancolombiaStats();
            if (response.success) setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const toggleMonth = (mes) => {
        setExpandedMonths(prev => ({ ...prev, [mes]: !prev[mes] }));
    };

    const fmtPesos = (v) => `$${Math.round(v).toLocaleString('es-CO')}`;
    const fmtMes = (mes) => {
        const [y, m] = mes.split('-');
        const names = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return `${names[parseInt(m)]} ${y}`;
    };
    const fmtDia = (dia) => {
        const [y, m, d] = dia.split('-');
        const names = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return `${parseInt(d)} ${names[parseInt(m)]}`;
    };

    const fetchMovements = async (page) => {
        try {
            setLoading(true);
            const offset = (page - 1) * limit;
            // Pasar filtros al servicio (necesita actualización en walletService si se usa objeto,
            // pero getBancolombiaMovements acepta args fijos. Debemos actualizar la llamada)
            // IMPORTANTE: Modificaré getBancolombiaMovements en walletService para aceptar opciones o modificar aquí la llamada.
            // Asumo que getBancolombiaMovements acepta (limit, offset, filters) o similar.
            // Revisando la firma original: getBancolombiaMovements(limit, offset).
            // Necesitaré actualizar parameters. Puesto que no puedo ver walletService ahora,
            // pasaré params adicionales esperando que JS lo maneje o actualizando service luego.
            // Mejor: Pasaré un objeto de opciones como tercer argumento.

            const filters = {
                startDate,
                endDate,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                tag: tagFilter || undefined,
                linkedSearch: unifiedSearch || undefined,
                amount: filterAmount || undefined,
                search: searchTerm || undefined
            };
            const response = await getBancolombiaMovements(limit, offset, filters);

            if (response.success) {
                setMovements(response.data);
                setTotal(response.total);
            }
        } catch (error) {
            console.error('Error fetching movements:', error);
            toast.error('Error al cargar movimientos');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Por favor selecciona un archivo CSV');
            return;
        }

        try {
            setUploading(true);
            const response = await uploadBancolombiaCSV(file);
            if (response.success) {
                toast.success(response.message);
                setFile(null);
                const fileInput = document.getElementById('csv-upload');
                if (fileInput) fileInput.value = '';
                // Volver a la página 1
                setCurrentPage(1);
                fetchMovements(1);
            } else {
                toast.error(response.message || 'Error al procesar el archivo');
            }
        } catch (error) {
            console.error('Error uploading CSV:', error);
            toast.error('Error al subir el archivo');
        } finally {
            setUploading(false);
        }
    };

    const handleLinkOrder = async (movementId, orderOrId, forceReconciliation = false) => {
        try {
            const orderId = orderOrId?.id || orderOrId;

            await linkOrderToMovement(movementId, orderId, forceReconciliation);

            if (forceReconciliation) {
                toast.success('🔗 Vinculado para conciliación contable (sin nuevo cobro)', { duration: 4000 });
            }

            // Recargar movimientos para obtener totales actualizados
            await fetchMovements(currentPage);
        } catch (error) {
            // Interceptar error ORDER_COVERED para ofrecer reconciliación
            const errData = error?.response?.data;
            if (errData?.error === 'ORDER_COVERED') {
                const orderId = orderOrId?.id || orderOrId;
                const saldo = errData.remaining_balance ?? 0;
                const confirmed = window.confirm(
                    `⚠️ Este pedido ya está cubierto (Saldo: $${saldo.toLocaleString('es-CO')}).\n\n` +
                    `El RC de Siigo ya registra el pago. ¿Deseas vincular este movimiento de Bancolombia ` +
                    `SOLO para conciliación contable?\n\n` +
                    `✅ Aceptar → Vincula sin generar nuevo cobro\n` +
                    `❌ Cancelar → No hacer nada`
                );
                if (confirmed) {
                    // Reintentar con reconciliation_only = true
                    await handleLinkOrder(movementId, orderId, true);
                }
            } else {
                console.error('Error linking order:', error);
                throw error;
            }
        }
    };

    const handleUnlinkOrder = async (movementId) => {
        try {
            await linkOrderToMovement(movementId, null);

            // Recargar movimientos para obtener totales actualizados
            await fetchMovements(currentPage);
        } catch (error) {
            console.error('Error unlinking order:', error);
            throw error;
        }
    };

    const handleLinkExpense = async (movementId, expense) => {
        try {
            const expenseId = expense?.id || expense;
            await linkExpenseToMovement(movementId, expenseId);
            await fetchMovements(currentPage);
        } catch (error) {
            console.error('Error linking expense:', error);
            throw error;
        }
    };

    const handleUnlinkExpense = async (movementId) => {
        try {
            await linkExpenseToMovement(movementId, null);
            await fetchMovements(currentPage);
        } catch (error) {
            console.error('Error unlinking expense:', error);
            throw error;
        }
    };

    const handleTagMovement = async (movementId, tag) => {
        try {
            const res = await tagMovementApi(movementId, tag);
            if (res.success) {
                toast.success(res.message);
                await fetchMovements(currentPage);
                fetchStats();
            }
        } catch (error) {
            console.error('Error tagging movement:', error);
            toast.error('Error al etiquetar');
        }
    };

    const handleBulkAutoTag = async () => {
        try {
            const res = await bulkAutoTagApi();
            if (res.success) {
                toast.success(res.message);
                await fetchMovements(currentPage);
                fetchStats();
            }
        } catch (error) {
            console.error('Error in bulk auto-tag:', error);
            toast.error('Error al auto-etiquetar');
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Búsqueda server-side (todos los filtros van al backend)
    const filteredMovements = movements;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <div className="mr-3 h-10 w-10 flex items-center justify-center bg-yellow-400 rounded-lg shadow-sm text-black">
                        <Landmark size={24} />
                    </div>
                    Pagos Bancolombia
                </h1>
                <p className="text-gray-500 mt-1">Carga los movimientos bancarios para realizar el seguimiento de ingresos.</p>
            </div>

            {/* Sección de Carga */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Upload className="mr-2 h-5 w-5 text-gray-500" />
                    Subir Nuevos Movimientos (CSV)
                </h2>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="flex-1 w-full">
                        <label
                            htmlFor="csv-upload"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 mb-3 text-gray-400" />
                                <p className="mb-2 text-sm text-gray-500">
                                    <span className="font-semibold">{file ? file.name : 'Click para subir'}</span>
                                </p>
                                <p className="text-xs text-gray-400">CSV o ZIP extraído de Bancolombia</p>
                            </div>
                            <input
                                id="csv-upload"
                                type="file"
                                className="hidden"
                                accept=".csv,.zip"
                                onChange={handleFileChange}
                            />
                        </label>
                    </div>

                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className={`w-full sm:w-auto h-12 px-8 rounded-lg font-medium flex items-center justify-center transition-all ${!file || uploading
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-primary-600 text-white hover:bg-primary-700 shadow-md active:scale-95'
                            }`}
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                                Procesando...
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="mr-2 h-5 w-5" />
                                Importar Movimientos
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* ===== Panel de Indicadores ===== */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
                <button
                    onClick={() => setStatsCollapsed(!statsCollapsed)}
                    className="w-full p-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                        <BarChart3 className="mr-2 h-5 w-5 text-indigo-500" />
                        Indicadores de Movimientos
                    </h2>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${statsCollapsed ? '' : 'rotate-180'}`} />
                </button>

                {!statsCollapsed && (
                    <div className="border-t border-gray-100">
                        {statsLoading ? (
                            <div className="p-8 flex items-center justify-center">
                                <Loader2 className="animate-spin h-6 w-6 text-indigo-500" />
                            </div>
                        ) : stats.length === 0 ? (
                            <div className="p-8 text-center text-gray-400 text-sm">Sin datos</div>
                        ) : (
                            <div>
                                {/* Header */}
                                <div className="grid grid-cols-12 gap-0 px-5 py-2.5 bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                    <div className="col-span-2">Período</div>
                                    <div className="col-span-2 text-center">Total</div>
                                    <div className="col-span-3 text-center text-green-700">↑ Entradas</div>
                                    <div className="col-span-3 text-center text-red-600">↓ Salidas</div>
                                    <div className="col-span-2 text-center">% Vinculado</div>
                                </div>

                                {stats.map((month) => {
                                    const isExpanded = expandedMonths[month.mes];
                                    const vincPct = month.entradas_count > 0
                                        ? Math.round((month.vinc_entradas_count / month.entradas_count) * 100)
                                        : 0;

                                    return (
                                        <div key={month.mes}>
                                            {/* Month row */}
                                            <button
                                                onClick={() => toggleMonth(month.mes)}
                                                className="w-full grid grid-cols-12 gap-0 px-5 py-3 hover:bg-indigo-50/50 transition-colors items-center border-b border-gray-50 cursor-pointer"
                                            >
                                                <div className="col-span-2 flex items-center font-semibold text-gray-800">
                                                    <ChevronDown className={`h-4 w-4 mr-1.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                                                    {fmtMes(month.mes)}
                                                    <span className="ml-2 text-xs text-gray-400 font-normal">{month.total_registros} reg</span>
                                                </div>
                                                <div className="col-span-2 text-center">
                                                    <span className="text-sm font-bold text-gray-700">{month.total_registros}</span>
                                                    <span className="block text-[10px] text-gray-400">{fmtPesos(month.entradas_pesos - month.salidas_pesos)}</span>
                                                </div>
                                                <div className="col-span-3 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <TrendingUp className="h-3.5 w-3.5 text-green-500" />
                                                        <span className="text-sm font-semibold text-green-700">{month.entradas_count}</span>
                                                    </div>
                                                    <span className="text-[10px] text-green-600">{fmtPesos(month.entradas_pesos)}</span>
                                                    <div className="flex justify-center gap-3 mt-0.5">
                                                        <span className="text-[9px] text-green-700 flex items-center"><Link2 className="h-2.5 w-2.5 mr-0.5" />{month.vinc_entradas_count} · {fmtPesos(month.vinc_entradas_pesos)}</span>
                                                        <span className="text-[9px] text-gray-400 flex items-center"><Unlink className="h-2.5 w-2.5 mr-0.5" />{month.novinc_entradas_count}</span>
                                                    </div>
                                                </div>
                                                <div className="col-span-3 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                                                        <span className="text-sm font-semibold text-red-600">{month.salidas_count}</span>
                                                    </div>
                                                    <span className="text-[10px] text-red-500">{fmtPesos(month.salidas_pesos)}</span>
                                                    <div className="flex justify-center gap-3 mt-0.5">
                                                        <span className="text-[9px] text-red-600 flex items-center"><Link2 className="h-2.5 w-2.5 mr-0.5" />{month.vinc_salidas_count} · {fmtPesos(month.vinc_salidas_pesos)}</span>
                                                        <span className="text-[9px] text-gray-400 flex items-center"><Unlink className="h-2.5 w-2.5 mr-0.5" />{month.novinc_salidas_count}</span>
                                                    </div>
                                                </div>
                                                <div className="col-span-2 flex flex-col items-center">
                                                    <span className="text-sm font-bold text-indigo-600">{vincPct}%</span>
                                                    <div className="w-full max-w-[80px] h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                                        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${vincPct}%` }} />
                                                    </div>
                                                    <span className="text-[9px] text-gray-400 mt-0.5">{month.vinc_entradas_count + month.vinc_salidas_count} de {month.total_registros}</span>
                                                </div>
                                            </button>

                                            {/* Day rows */}
                                            {isExpanded && month.dias.map((day) => {
                                                const dayVincPct = parseInt(day.entradas_count) > 0
                                                    ? Math.round((parseInt(day.vinc_entradas_count) / parseInt(day.entradas_count)) * 100)
                                                    : 0;
                                                return (
                                                    <div key={day.dia} className="grid grid-cols-12 gap-0 px-5 py-2 bg-gray-50/70 border-b border-gray-50 items-center text-sm">
                                                        <div className="col-span-2 pl-7 text-gray-500 font-medium">{fmtDia(day.dia)}</div>
                                                        <div className="col-span-2 text-center">
                                                            <span className="font-medium text-gray-600">{day.total_registros}</span>
                                                        </div>
                                                        <div className="col-span-3 text-center">
                                                            <span className="text-green-700 font-medium">{day.entradas_count}</span>
                                                            <span className="text-[10px] text-green-600 ml-1">{fmtPesos(parseFloat(day.entradas_pesos))}</span>
                                                            <div className="flex justify-center gap-2 mt-0.5">
                                                                <span className="text-[9px] text-green-700"><Link2 className="h-2.5 w-2.5 inline mr-0.5" />{day.vinc_entradas_count}</span>
                                                                <span className="text-[9px] text-gray-400"><Unlink className="h-2.5 w-2.5 inline mr-0.5" />{day.novinc_entradas_count}</span>
                                                            </div>
                                                        </div>
                                                        <div className="col-span-3 text-center">
                                                            <span className="text-red-600 font-medium">{day.salidas_count}</span>
                                                            <span className="text-[10px] text-red-500 ml-1">{fmtPesos(parseFloat(day.salidas_pesos))}</span>
                                                            <div className="flex justify-center gap-2 mt-0.5">
                                                                <span className="text-[9px] text-red-600"><Link2 className="h-2.5 w-2.5 inline mr-0.5" />{day.vinc_salidas_count}</span>
                                                                <span className="text-[9px] text-gray-400"><Unlink className="h-2.5 w-2.5 inline mr-0.5" />{day.novinc_salidas_count}</span>
                                                            </div>
                                                        </div>
                                                        <div className="col-span-2 flex flex-col items-center">
                                                            <div className="w-full max-w-[80px] h-1 bg-gray-200 rounded-full overflow-hidden">
                                                                <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${dayVincPct}%` }} />
                                                            </div>
                                                            <span className="text-[9px] text-gray-400">{dayVincPct}%</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Tag Summary - Desglose por concepto contable */}
                                            {isExpanded && month.tag_summary && month.tag_summary.length > 0 && (
                                                <div className="bg-indigo-50/40 border-t border-indigo-100">
                                                    <div className="px-5 py-2 flex items-center gap-2">
                                                        <span className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wider">🏷️ Desglose por Concepto</span>
                                                        <span className="text-[10px] text-indigo-400">({month.tag_summary.reduce((s, t) => s + t.count, 0)} movimientos)</span>
                                                    </div>
                                                    <div className="grid grid-cols-12 gap-0 px-5 py-1 text-[10px] font-semibold text-gray-500 uppercase">
                                                        <div className="col-span-4">Concepto</div>
                                                        <div className="col-span-2 text-center">Cant.</div>
                                                        <div className="col-span-2 text-right text-green-700">Entradas</div>
                                                        <div className="col-span-2 text-right text-red-600">Salidas</div>
                                                        <div className="col-span-2 text-right">Neto</div>
                                                    </div>
                                                    {month.tag_summary.map((ts) => {
                                                        const cfg = TAG_CONFIG[ts.tag] || { label: ts.tag, icon: '🔹', bg: 'bg-gray-100', text: 'text-gray-700' };
                                                        return (
                                                            <div key={ts.tag} className="grid grid-cols-12 gap-0 px-5 py-1.5 border-t border-indigo-50 items-center hover:bg-indigo-50/60 transition-colors">
                                                                <div className="col-span-4 flex items-center gap-1.5">
                                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text} font-medium`}>
                                                                        {cfg.icon} {cfg.label}
                                                                    </span>
                                                                </div>
                                                                <div className="col-span-2 text-center text-sm font-medium text-gray-700">{ts.count}</div>
                                                                <div className="col-span-2 text-right text-[11px] text-green-700 font-medium">
                                                                    {ts.entradas > 0 ? fmtPesos(ts.entradas) : '-'}
                                                                </div>
                                                                <div className="col-span-2 text-right text-[11px] text-red-600 font-medium">
                                                                    {ts.salidas > 0 ? fmtPesos(ts.salidas) : '-'}
                                                                </div>
                                                                <div className={`col-span-2 text-right text-[11px] font-bold ${ts.neto >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                                                    {ts.neto >= 0 ? '+' : '-'}{fmtPesos(Math.abs(ts.neto))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {/* Total row */}
                                                    <div className="grid grid-cols-12 gap-0 px-5 py-2 border-t border-indigo-200 bg-indigo-50/80 font-semibold">
                                                        <div className="col-span-4 text-[11px] text-indigo-700">Total Etiquetados</div>
                                                        <div className="col-span-2 text-center text-sm text-indigo-700">
                                                            {month.tag_summary.reduce((s, t) => s + t.count, 0)}
                                                        </div>
                                                        <div className="col-span-2 text-right text-[11px] text-green-700">
                                                            {fmtPesos(month.tag_summary.reduce((s, t) => s + t.entradas, 0))}
                                                        </div>
                                                        <div className="col-span-2 text-right text-[11px] text-red-600">
                                                            {fmtPesos(month.tag_summary.reduce((s, t) => s + t.salidas, 0))}
                                                        </div>
                                                        <div className="col-span-2 text-right text-[11px] text-indigo-700">
                                                            {(() => {
                                                                const neto = month.tag_summary.reduce((s, t) => s + t.neto, 0);
                                                                return `${neto >= 0 ? '+' : '-'}${fmtPesos(Math.abs(neto))}`;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Listado de Movimientos */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100 grid grid-cols-1 md:grid-cols-12 gap-4 items-end bg-gray-50/50">
                    {/* Filtros */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Fecha Inicio</label>
                        <div className="relative">
                            <input
                                type="date"
                                className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Fecha Fin</label>
                        <div className="relative">
                            <input
                                type="date"
                                className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Estado / Conciliación</label>
                        <div className="relative">
                            <select
                                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none appearance-none text-sm bg-white"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="all">Todos los Movimientos</option>
                                <option value="linked">✅ Vinculados (Cualquiera)</option>
                                <option value="pending">⏳ Pendientes (Por Vincular)</option>
                                <option value="pending_income">⏳📥 Entradas sin Vincular</option>
                                <option value="pending_expense">⏳📤 Salidas sin Vincular</option>
                                <option value="linked_income">✅📥 Entradas Vinculadas</option>
                                <option value="linked_expense">✅📤 Salidas Vinculadas</option>
                                <option value="tagged">🏷️ Solo Etiquetados</option>
                            </select>
                            <Filter className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Filtro por Etiqueta Específica */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Filtrar por Etiqueta</label>
                        <div className="relative">
                            <select
                                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none appearance-none text-sm bg-indigo-50/30 font-medium text-indigo-900 placeholder-indigo-400"
                                value={tagFilter}
                                onChange={(e) => { setTagFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="">Cualquier Etiqueta</option>
                                {Object.entries(TAG_CONFIG).map(([key, cfg]) => (
                                    <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-400 pointer-events-none" />
                        </div>
                    </div>



                    {/* Búsqueda Unificada (Pedido/Cliente) */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Búsqueda Vinculación</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Ref Pedido o Cliente..."
                                className="w-full pl-9 pr-8 py-2 border border-blue-100 bg-blue-50/50 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-medium text-gray-800 placeholder-gray-400"
                                value={unifiedSearch}
                                onChange={(e) => setUnifiedSearch(e.target.value)}
                            />
                            {unifiedSearch && (
                                <button
                                    onClick={() => setUnifiedSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
                                >
                                    <X className="h-3 w-3 text-gray-500" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filtro Monto */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Monto Exacto</label>
                        <input
                            type="number"
                            placeholder="$ Valor..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
                            value={filterAmount}
                            onChange={(e) => { setFilterAmount(e.target.value); setCurrentPage(1); }}
                        />
                    </div>

                    {/* Búsqueda General (Legacy/Ref) */}
                    <div className="md:col-span-1 border-l border-gray-200 pl-4 ml-2"></div>

                    <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">Buscar en Tabla</label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Ref o Descripción..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {(startDate || endDate || statusFilter !== 'all' || unifiedSearch || filterAmount) && (
                        <div className="md:col-span-12 flex justify-between items-center mt-2">
                            <button
                                onClick={handleBulkAutoTag}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center bg-blue-50 px-3 py-1 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                                🏷️ Auto-etiquetar Intereses/Comisiones
                            </button>
                            <button
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setStatusFilter('all');
                                    setUnifiedSearch('');
                                    setFilterAmount('');
                                    setSearchTerm('');
                                    setCurrentPage(1);
                                }}
                                className="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center"
                            >
                                <X className="h-3 w-3 mr-1" />
                                Limpiar filtros
                            </button>
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto min-h-[400px]">
                    {loading ? (
                        <div className="p-12 flex flex-col items-center justify-center">
                            <Loader2 className="animate-spin h-8 w-8 text-primary-600 mb-2" />
                            <p className="text-gray-500">Cargando movimientos...</p>
                        </div>
                    ) : filteredMovements.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="bg-gray-100 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FileText className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-gray-900 font-medium">No se encontraron movimientos</h3>
                            <p className="text-gray-500 mt-1">Sube un archivo CSV para comenzar.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-600 text-sm font-semibold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Fecha</th>
                                    <th className="px-6 py-4">Descripción / Referencia</th>
                                    <th className="px-6 py-4">Documento</th>
                                    <th className="px-6 py-4 w-64">Vinculación</th>
                                    <th className="px-6 py-4 text-center">Conciliación</th>
                                    <th className="px-6 py-4 text-right">Valor Movimiento</th>
                                </tr>
                                {/* Header Limpio - Filtros movidos arriba */}
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredMovements.map((m) => (
                                    <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className="text-gray-900 font-medium">
                                                    {(() => {
                                                        if (!m.fecha) return 'Sin fecha';
                                                        const dateStr = typeof m.fecha === 'string' ? m.fecha.substring(0, 10) : '';

                                                        if (dateStr.length === 10 && dateStr.includes('-')) {
                                                            const [year, month, day] = dateStr.split('-').map(Number);
                                                            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                                                                return format(new Date(year, month - 1, day), 'dd MMM yyyy', { locale: es });
                                                            }
                                                        }
                                                        try {
                                                            return format(new Date(m.fecha), 'dd MMM yyyy', { locale: es });
                                                        } catch (e) {
                                                            return 'Fecha inválida';
                                                        }
                                                    })()}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {m.comprobante || 'Sin comprobante'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <p className="text-gray-900 font-medium line-clamp-1">{m.descripcion}</p>
                                            <div className="flex items-center text-xs text-gray-500 mt-1">
                                                <Calendar className="mr-1 h-3 w-3" />
                                                Ref: {m.referencia || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {m.documento || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {m.tag ? (
                                                <div className="flex items-center gap-1">
                                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${(TAG_CONFIG[m.tag]?.bg || 'bg-gray-100')} ${(TAG_CONFIG[m.tag]?.text || 'text-gray-700')}`}>
                                                        {(TAG_CONFIG[m.tag]?.icon || '🔹')} {(TAG_CONFIG[m.tag]?.label || m.tag)}
                                                    </span>
                                                    <button
                                                        onClick={() => handleTagMovement(m.id, null)}
                                                        className="text-gray-400 hover:text-red-500 p-0.5"
                                                        title="Quitar etiqueta"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ) : m.valor >= 0 ? (
                                                <OrderSearchSelect
                                                    currentOrderId={m.pedido_id}
                                                    currentOrderData={m.pedido_id ? {
                                                        order_number: m.order_number,
                                                        customer_name: m.customer_name
                                                    } : null}
                                                    onLink={(orderId) => handleLinkOrder(m.id, orderId)}
                                                    onUnlink={() => handleUnlinkOrder(m.id)}
                                                />
                                            ) : m.payment_group_id ? (
                                                <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded">
                                                    <span className="text-xl">🔗</span>
                                                    <div className="flex-1">
                                                        <span className="text-xs font-bold text-orange-800 block">Pago Compartido a Gastos</span>
                                                        <span className="text-[10px] text-orange-600">ID Grupo: {m.payment_group_id}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-1">
                                                    <ExpenseSearchSelect
                                                        currentExpenseId={m.expense_id || m.linked_expense_id}
                                                        currentExpenseData={m.linked_expense_id ? {
                                                            expense_provider: m.expense_provider,
                                                            expense_amount: m.expense_amount,
                                                            expense_concept: m.expense_concept,
                                                            expense_cost_center: m.expense_cost_center,
                                                            expense_invoice_number: m.expense_invoice_number
                                                        } : null}
                                                        onLink={(expense) => handleLinkExpense(m.id, expense)}
                                                        onUnlink={() => handleUnlinkExpense(m.id)}
                                                        movementAmount={m.valor}
                                                    />
                                                    {/* Mostrar botón de múltiples facturas solo si no está vinculado 1:1 */}
                                                    {!m.expense_id && !m.linked_expense_id && (
                                                        <button 
                                                            onClick={() => { setSelectedMovementForMulti(m); setMultiExpenseModalOpen(true); }}
                                                            className="w-full mt-1 text-[10px] py-1 bg-white border border-dashed border-orange-300 text-orange-600 rounded hover:bg-orange-50 truncate"
                                                        >
                                                            + Múltiples FCs/Gastos
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {/* Quick tag button for small unlinked movements */}
                                            {!m.tag && !m.pedido_id && !m.expense_id && Math.abs(m.valor) < 5000 && (
                                                <select
                                                    className="mt-1 text-[10px] border border-dashed border-gray-300 rounded px-1 py-0.5 bg-gray-50 cursor-pointer"
                                                    defaultValue=""
                                                    onChange={(e) => { if (e.target.value) handleTagMovement(m.id, e.target.value); }}
                                                >
                                                    <option value="" disabled>🏷️ Etiquetar como...</option>
                                                    {Object.entries(TAG_CONFIG).map(([key, cfg]) => (
                                                        <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {/* Quick tag for ANY unlinked movement (larger amounts) */}
                                            {!m.tag && !m.pedido_id && !m.expense_id && Math.abs(m.valor) >= 5000 && (
                                                <select
                                                    className="mt-1 text-[10px] border border-dashed border-gray-300 rounded px-1 py-0.5 bg-white cursor-pointer opacity-50 hover:opacity-100"
                                                    defaultValue=""
                                                    onChange={(e) => { if (e.target.value) handleTagMovement(m.id, e.target.value); }}
                                                >
                                                    <option value="" disabled>🏷️ Etiquetar...</option>
                                                    {Object.entries(TAG_CONFIG).map(([key, cfg]) => (
                                                        <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {m.pedido_id ? (
                                                (() => {
                                                    // Datos Globales
                                                    const orderTotal = parseFloat(m.order_total_amount) || 0;
                                                    const totalPaidGlobal = (parseFloat(m.total_bank_paid) || 0) + (parseFloat(m.order_cash_paid) || 0);

                                                    // Datos Históricos (Snapshot en el momento de este pago)
                                                    const accumAtPoint = (parseFloat(m.accum_paid_at_this_point) || 0) + (parseFloat(m.order_cash_paid) || 0);
                                                    const remainingAtPoint = orderTotal - accumAtPoint;

                                                    // Tolerancia de $100 pesos para ignorar decimales insignificantes
                                                    const isFullyPaidAtPoint = remainingAtPoint <= 100;

                                                    return (
                                                        <div className="flex flex-col items-center gap-1">
                                                            <span className="text-[10px] text-gray-400 mb-0.5">
                                                                Pedido: ${new Intl.NumberFormat('es-CO').format(Math.round(orderTotal))}
                                                            </span>

                                                            {/* Estado Histórico del Saldo */}
                                                            {isFullyPaidAtPoint ? (
                                                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold border border-green-200 flex items-center shadow-sm">
                                                                    ✅ Saldado
                                                                    {remainingAtPoint < -100 && <span className="ml-1 text-[9px] opacity-75">(+{Math.round(Math.abs(remainingAtPoint))})</span>}
                                                                </span>
                                                            ) : (
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <span className="text-[10px] bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium border border-orange-200 flex items-center">
                                                                        📉 Restaban: ${new Intl.NumberFormat('es-CO').format(Math.round(remainingAtPoint))}
                                                                    </span>

                                                                    {/* Bandera de "Ya cubierto globalmente" para tranquilidad */}
                                                                    {orderTotal - totalPaidGlobal <= 100 && (
                                                                        <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100 font-medium">
                                                                            ✨ Hoy: ¡Totalmente Pago!
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Progreso Visual */}
                                                            <div className="w-16 h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                                                <div
                                                                    className={`h-full ${isFullyPaidAtPoint ? 'bg-green-500' : 'bg-orange-400'}`}
                                                                    style={{ width: `${Math.min((accumAtPoint / orderTotal) * 100, 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-gray-400 text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            <span className={`text-sm font-bold ${m.valor < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                {m.valor < 0 ? '-' : '+'} $ {Math.abs(m.valor).toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer Paginación */}
                <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                        Mostrando {((currentPage - 1) * limit) + 1} a {Math.min(currentPage * limit, total)} de {total} resultados
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border rounded">
                            Página {currentPage} de {totalPages || 1}
                        </span>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal para Pago Múltiple de Gastos */}
            <MultiExpenseModal 
                isOpen={multiExpenseModalOpen}
                onClose={() => setMultiExpenseModalOpen(false)}
                onSuccess={() => {
                    fetchMovements();
                    fetchStats();
                    setMultiExpenseModalOpen(false);
                }}
                movement={selectedMovementForMulti}
            />
        </div>
    );
};

export default BancolombiaMovementsPage;
